/**
 * Where the active session lives between launches: `persist`'s storage over an
 * async `KeyValueStorage` — Capacitor Preferences in the app (plan D21:
 * Android may evict a WebView's localStorage and IndexedDB, and a logged set
 * must not live there).
 *
 * Three failures this file exists to make LOUD instead of silent (D22):
 *
 * ── A READ THAT FAILED IS NOT AN EMPTY SESSION ─────────────────────────────
 * If `getItem` throws, the store must not come up empty and then WRITE that
 * emptiness over the session still on disk at the next change. So a failed
 * read (after a few tries) hydrates empty but switches every later write off
 * for the life of the process, and says so. The stored session survives for
 * the next launch; nothing logged in this one is persisted, which is the
 * lesser loss and the visible one.
 *
 * ── A VALUE THAT DOES NOT PARSE IS SET ASIDE, NEVER OVERWRITTEN ────────────
 * It is copied to `<key>.corrupt-<ISO>` and reported before the store starts
 * empty, as T21's outbox store does with its own value.
 *
 * ── WRITES LAND IN THE ORDER THEY WERE MADE ────────────────────────────────
 * `persist` calls `setItem` on every change without awaiting the last one; on
 * an async bridge two writes could land swapped and the OLDER state win. They
 * run on one promise chain here.
 */
import type { KeyValueStorage } from "@repo/client/i18n";
import type { SwapScope } from "@repo/client/session";
import type { SwapReason } from "@repo/client/swap";
import { exerciseClip, exerciseTracking } from "@repo/contract/catalog";
import {
  planItemView,
  sessionAdjustment,
  sessionReadiness,
  setKind,
  setSide,
  slotCandidate,
} from "@repo/contract/training";
import { z } from "zod";
import type { PersistStorage, StorageValue } from "zustand/middleware";
import type { WorkoutSession } from "./session-model";

export const WORKOUT_STORAGE_KEY = "mypocketgym.workout";

/** Bumped with every change to the stored shape; `migrate` handles the old. */
export const WORKOUT_STORE_VERSION = 1;

const READ_ATTEMPTS = 3;

const setBefore = z.object({
  distanceMetres: z.number().nullable(),
  durationSeconds: z.number().nullable(),
  // Additive inside v1 (`D209`), like every optional key below.
  kind: setKind.optional(),
  load: z.number().nullable(),
  reps: z.number().nullable(),
  rpe: z.number().nullable(),
  side: setSide.optional(),
});

const loggedSet = z.object({
  // Additive INSIDE v1 (T36), so a session stored without it still parses and
  // `WORKOUT_STORE_VERSION` does not move: a version this build does not know
  // is set aside, which would hide a running workout.
  before: setBefore.optional(),
  distanceMetres: z.number().nullable(),
  durationSeconds: z.number().nullable(),
  exerciseId: z.string().min(1),
  id: z.string().min(1),
  // `D209`, additive inside v1: a set stored without them is a working set
  // on both sides — and WITHOUT them here, `z.object` would strip a
  // warm-up's kind at the first relaunch, and its record would replay as a
  // working set.
  kind: setKind.optional(),
  load: z.number().nullable(),
  performedAt: z.number(),
  reps: z.number().nullable(),
  rpe: z.number().nullable(),
  side: setSide.optional(),
  supersedes: z.array(z.string()),
});

const DIGITS = /^\d+$/;

/** A record floor (`D243`), as `live-records.ts` folds it. */
const recordFloor = z.object({
  distance: z.number().nullable(),
  duration: z.number().nullable(),
  e1rm: z.number().nullable(),
  load: z.number().nullable(),
  repMaxes: z.array(z.number().nullable()),
  reps: z.number().nullable(),
  volume: z.number().nullable(),
});

const byIndex = <T extends z.ZodType>(value: T) =>
  z.record(z.string().regex(DIGITS), value);

const swapReason = z.enum([
  "busy",
  "discomfort",
  "dislike",
  "variety",
]) satisfies z.ZodType<SwapReason>;

const swapScope = z.enum([
  "today",
  "plan",
  "never",
]) satisfies z.ZodType<SwapScope>;

/** The v1 session, as written by this build. */
export const storedSessionV1 = z.object({
  adjustments: z.array(sessionAdjustment),
  adjustmentsSettled: z.boolean(),
  // `D254`, additive inside v1: a blank workout waits for its next exercise
  // instead of ending at its last set. An older build strips it on an OTA
  // rollback, and the session then ends the way every other one does.
  blank: z.literal(true).optional(),
  // `D244`, additive inside v1: the values answered "Sim". An older build
  // strips them on an OTA rollback, which asks a question once more.
  confirmed: z.array(z.string()).optional(),
  // `D209`, additive inside v1, every one: the kind sheet's choice,
  // "Preencher abaixo" and the calculator's warm-ups. An older build strips
  // them on an OTA rollback, which loses the plan for the sets not yet
  // logged and never a set. Its `bests` is now `records` (`D243`); a stored
  // `bests` is stripped here, and the history refills `records`.
  // Additive inside v1 (`D247`): an older build strips it on an OTA
  // rollback, and the offer to carry today into the plan is asked again.
  carried: z.literal(true).optional(),
  // Additive INSIDE v1 (D181), like `before` and `clip`: `deferred`,
  // `swapScopes`, and a swap's `primaryMuscleId` and `reason`. A session
  // stored by a build without them still parses, and the version stays put.
  deferred: z.array(z.int().nonnegative()).optional(),
  draftKind: z.union([setKind, z.literal("working")]).optional(),
  draftSide: setSide.optional(),
  ended: z.enum(["complete", "early"]).nullable(),
  endedAt: z.number().nullable(),
  exIndex: z.int().nonnegative(),
  extra: byIndex(z.int()),
  feedback: z.enum(["pending", "sent", "skipped"]),
  fill: byIndex(
    z.object({ load: z.number().nullable(), second: z.number() })
  ).optional(),
  items: z.array(planItemView),
  // `D245`, additive inside v1, like the two below: the forgotten-workout
  // answer and the clock's pauses. An older build strips them on an OTA
  // rollback, which forgets a pause — the duration reads as unpaused —
  // never a set.
  keptOpenAt: z.number().optional(),
  kg: z.number().nullable(),
  label: z.string().nullable(),
  logged: byIndex(z.array(loggedSet)),
  order: z.array(z.int().nonnegative()),
  // Additive inside v1, like `swapScopes`: an older build strips it on an OTA
  // rollback, which loses only the owed exclusion, never the session.
  owedExclusions: z.array(z.int().nonnegative()).optional(),
  pauses: z
    .array(z.object({ from: z.number(), to: z.number().optional() }))
    .optional(),
  phase: z.enum(["log", "rest", "done"]),
  planSessionId: z.string().nullable(),
  previous: z.record(
    z.string(),
    z.array(
      z.object({ load: z.number().nullable(), reps: z.number().nullable() })
    )
  ),
  // `D249`, additive inside v1: a quick workout's swaps. An older build
  // strips it on an OTA rollback; the session then runs on as an unplanned
  // one, and only its picker loses the offline swaps.
  quick: z
    .object({ alternatives: z.record(z.string(), z.array(slotCandidate)) })
    .optional(),
  readiness: sessionReadiness.nullable(),
  // `D243`, additive inside v1: the records the live alert compares with.
  // Stripped by an older build, which refetches its own `bests`.
  records: z.record(z.string(), recordFloor).optional(),
  reps: z.number(),
  restEndsAt: z.number().nullable(),
  restTotal: z.number(),
  sessionId: z.string().min(1),
  setIndex: z.int().nonnegative(),
  startedAt: z.number(),
  swapScopes: byIndex(swapScope).optional(),
  swaps: byIndex(
    z.object({
      // Additive INSIDE v1 (T39), like `before` above: a swap stored by a
      // build without it still parses, and `WORKOUT_STORE_VERSION` stays put.
      clip: exerciseClip.nullable().optional(),
      exerciseId: z.string().min(1),
      name: z.string().optional(),
      primaryMuscleId: z.int().positive().optional(),
      reason: swapReason.optional(),
      // Additive inside v1 too: without it here a timed substitute would be
      // stripped back to reps by the first relaunch.
      tracks: exerciseTracking.optional(),
    })
  ),
  warmups: byIndex(
    z.array(z.object({ load: z.number().nullable(), reps: z.int().positive() }))
  ).optional(),
});

export interface PersistedWorkout {
  readonly session: WorkoutSession | null;
}

const storedStateV1 = z.object({ session: storedSessionV1.nullable() });

export interface WorkoutStorageReport {
  readonly reason: string;
  /** Where the raw value was preserved, when it was. */
  readonly savedAs?: string;
}

export interface WorkoutStorageOptions {
  readonly now?: () => Date;
  /** A value set aside, or the storage refusing to answer. */
  readonly onProblem?: (report: WorkoutStorageReport) => void;
}

export interface WorkoutPersistStorage
  extends PersistStorage<PersistedWorkout, Promise<void>> {
  /**
   * `persist`'s `migrate`. Today there is only v1, so any other version is a
   * shape this build cannot read — a newer build's, after an OTA rollback. It
   * is set aside rather than half-read, and the store starts empty; if the
   * copy fails, writes go off so the original is never overwritten. The next
   * shape change adds its step here (v1 → v2) and must keep a v1 session.
   */
  readonly migrate: (
    state: unknown,
    version: number
  ) => Promise<PersistedWorkout>;
  /** `true` once a read failed: writes are off until the next launch. */
  readonly readOnly: () => boolean;
}

/** A v1 state, validated; `null` when it is not one. */
export function parseStoredV1(state: unknown): PersistedWorkout | null {
  const parsed = storedStateV1.safeParse(state);
  return parsed.success ? (parsed.data as PersistedWorkout) : null;
}

export function createWorkoutStorage(
  storage: KeyValueStorage,
  options: WorkoutStorageOptions = {}
): WorkoutPersistStorage {
  const clock = options.now ?? (() => new Date());
  let readFailed = false;
  let chain: Promise<unknown> = Promise.resolve();

  const setAside = async (name: string, raw: string, reason: string) => {
    const savedAs = `${name}.corrupt-${clock().toISOString()}`;
    try {
      await storage.setItem(savedAs, raw);
      options.onProblem?.({ reason, savedAs });
    } catch (error) {
      // The copy failed too: keep the original where it is, untouched, and
      // write nothing over it for the rest of this run.
      readFailed = true;
      options.onProblem?.({
        reason: `${reason}; the copy failed: ${String(error)}`,
      });
    }
  };

  const readRaw = async (name: string): Promise<string | null> => {
    let lastError: unknown;
    for (let attempt = 0; attempt < READ_ATTEMPTS; attempt += 1) {
      try {
        return await storage.getItem(name);
      } catch (error) {
        lastError = error;
      }
    }
    throw lastError;
  };

  return {
    async getItem(name) {
      let raw: string | null;
      try {
        raw = await readRaw(name);
      } catch (error) {
        readFailed = true;
        options.onProblem?.({
          reason: `read failed, writes disabled until relaunch: ${String(error)}`,
        });
        return null;
      }
      if (raw === null) {
        return null;
      }
      let json: unknown;
      try {
        json = JSON.parse(raw);
      } catch (error) {
        await setAside(name, raw, `invalid JSON: ${String(error)}`);
        return null;
      }
      const envelope = z
        .object({ state: z.unknown(), version: z.number().optional() })
        .safeParse(json);
      if (!envelope.success) {
        await setAside(name, raw, envelope.error.message);
        return null;
      }
      const version = envelope.data.version ?? 0;
      if (version !== WORKOUT_STORE_VERSION) {
        // Another shape: `migrate` decides. It sets aside what it cannot read.
        return {
          state: envelope.data.state as PersistedWorkout,
          version,
        };
      }
      const state = parseStoredV1(envelope.data.state);
      if (state === null) {
        await setAside(name, raw, "the stored session does not match v1");
        return null;
      }
      return { state, version };
    },
    async migrate(state, version) {
      const current =
        version === WORKOUT_STORE_VERSION ? parseStoredV1(state) : null;
      if (current !== null) {
        return current;
      }
      await setAside(
        WORKOUT_STORAGE_KEY,
        JSON.stringify({ state, version }),
        `stored version ${version} is not readable by v${WORKOUT_STORE_VERSION}`
      );
      return { session: null };
    },
    readOnly: () => readFailed,
    removeItem(name) {
      const run = chain.then(() =>
        readFailed ? undefined : storage.removeItem(name)
      );
      chain = run.catch(() => undefined);
      return run;
    },
    setItem(name, value: StorageValue<PersistedWorkout>) {
      const run = chain.then(async () => {
        if (readFailed) {
          return;
        }
        await storage.setItem(name, JSON.stringify(value));
      });
      chain = run.catch((error: unknown) => {
        options.onProblem?.({ reason: `write failed: ${String(error)}` });
      });
      return run;
    },
  };
}
